import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { cn } from '../lib/utils';
import { useTranslation } from 'react-i18next';
import { contractsAPI, companiesAPI, contractTypesAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Trash2,
  Eye,
  FileText,
  Clock,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  FilterX,
  Filter,
  Info,
  Pencil,
  Calendar as CalendarIcon,
  UploadCloud,
  X
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { tr, de, enUS } from 'date-fns/locale';

// Filtre satırı için: değer varken içinde "x" temizleme ikonu gösteren input.
// Modül seviyesinde tanımlı (render içinde DEĞİL) — aksi halde her tuş vuruşunda focus kaybolur.
const ClearableFilterInput = ({ value, onChange, onClear, placeholder, className = '', leftIcon = null, clearLabel = '' }) => (
  <div className="relative">
    {leftIcon}
    <Input
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`h-8 text-sm bg-white border-blue-300 focus-visible:border-blue-500 focus-visible:ring-blue-400/40 dark:bg-[#333333] dark:border-blue-700 dark:text-white dark:placeholder:text-gray-500 ${value ? 'pr-7' : ''} ${className}`}
    />
    {value && (
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClear}
        aria-label={clearLabel}
        title={clearLabel}
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-blue-400 hover:text-blue-600 hover:bg-blue-100 dark:text-blue-500 dark:hover:text-blue-300 dark:hover:bg-blue-900/40 transition-colors"
        tabIndex={-1}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

export default function Contracts() {
  const { t, i18n } = useTranslation();

  const currentLocale = i18n.language === 'tr' ? tr : i18n.language === 'de' ? de : enUS;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [contracts, setContracts] = useState([]);
  const [totalFound, setTotalFound] = useState(0);
  const [totalTotal, setTotalTotal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    customer: '',
    days: '',
    time: '',
    operator: '',
    contractType: '',
    company: '',
    orderNo: '',
    contractNo: '',
    description: ''
  });

  const [includeExpired, setIncludeExpired] = useState(false);
  const [includeExtended, setIncludeExtended] = useState(false);
  const handleToggleIncludeExpired = (checked) => {
    const next = checked === true;
    setIncludeExpired(next);
    if (next) setIncludeExtended(false);
  };

  const handleToggleIncludeExtended = (checked) => {
    const next = checked === true;
    setIncludeExtended(next);
    if (next) setIncludeExpired(false);
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Detail modal
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  // Edit Modal States
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  // Sorting
  const [sortField, setSortField] = useState('days_remaining');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // When filters/toggles/sort/pageSize change:
    // - if already on page 1, refetch page 1
    // - otherwise jump to page 1 (page change effect will refetch)
    if (currentPage === 1) {
      fetchContractsPage(1);
    } else {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, includeExpired, includeExtended, sortField, sortDirection, itemsPerPage]);

  useEffect(() => {
    fetchContractsPage(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const contractId = searchParams.get('id');
    if (contractId) {
      // Open detail directly (contract may not be on current page)
      handleViewDetail({ id: contractId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [companiesRes, typesRes] = await Promise.all([
        companiesAPI.getAll(),
        contractTypesAPI.getAll(),
      ]);
      setCompanies(companiesRes.data);
      setContractTypes(typesRes.data);
      // Load first page of contracts with current filters
      await fetchContractsPage(1);
    } catch (error) {
      toast.error(t('contracts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchContractsPage = async (page) => {
    try {
      const p = Number(page) || 1;
      const skip = (p - 1) * itemsPerPage;
      const res = await contractsAPI.getPaged({
        skip,
        limit: itemsPerPage,
        include_expired: includeExpired,
        extended: includeExtended ? true : undefined,
        customer: filters.customer || undefined,
        days: filters.days || undefined,
        time: filters.time || undefined,
        operator: filters.operator || undefined,
        contractType: filters.contractType || undefined,
        company: filters.company || undefined,
        orderNo: filters.orderNo || undefined,
        contractNo: filters.contractNo || undefined,
        description: filters.description || undefined,
        sortField,
        sortDirection,
      });

      let contractsList = res?.data?.items || [];

      setContracts(contractsList);
      setTotalFound(Number(res?.data?.total_found || 0));
      setTotalTotal(Number(res?.data?.total_total || 0));
    } catch (e) {
      // Avoid double toasts; show one clear message
      toast.error(t('contracts.loadError'));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      customer: '',
      days: '',
      time: '',
      operator: '',
      contractType: '',
      company: '',
      orderNo: '',
      contractNo: '',
      description: ''
    });
    setSortField('days_remaining');
    setSortDirection('asc');
    setIncludeExpired(false);
    setIncludeExtended(false);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = (contract) => {
    setContractToDelete(contract);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contractToDelete) return;

    try {
      await contractsAPI.delete(contractToDelete.id);
      toast.success(t('contracts.deleteSuccess'));
      setDeleteModalOpen(false);
      setContractToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(t('contracts.deleteError'));
    }
  };

  const handleEdit = async (contract) => {
    try {
      // Fetch fresh data for the contract to get latest files
      const response = await contractsAPI.getById(contract.id);
      setEditingContract({ ...response.data });
      setSelectedFiles([]);
      setDragActive(false);
      setIsEditOpen(true);
    } catch (error) {
      console.error('Error fetching contract for edit:', error);
      // Fallback to existing data
      setEditingContract({ ...contract });
      setSelectedFile(null);
      setIsEditOpen(true);
    }
  };

  const handleDeleteExistingFile = async (fileId) => {
    if (!editingContract) return;

    try {
      await contractsAPI.deleteFile(editingContract.id, fileId);
      toast.success(t('contracts.fileDeleted', { defaultValue: 'File deleted successfully' }));

      // Update local state
      setEditingContract({
        ...editingContract,
        files: (editingContract.files || []).filter(f => f.id !== fileId)
      });

      // Also refresh the background list
      fetchData();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(t('contracts.deleteFileError', { defaultValue: 'Error deleting file' }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const handleSaveChanges = async () => {
    if (!editingContract) return;

    try {
      // 1. Update contract details
      const updateData = {
        contract_type_id: editingContract.contract_type_id,
        company_id: editingContract.company_id,
        start_date: editingContract.start_date,
        end_date: editingContract.end_date,
        order_number: editingContract.order_number,
        contract_number: editingContract.contract_number,
        description: editingContract.description,
        extended: editingContract.extended === true,
        extended_months: editingContract.extended_months ?? null,
      };

      await contractsAPI.update(editingContract.id, updateData);

      // 2. Upload files if any
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          await contractsAPI.uploadFile(editingContract.id, file);
        }
      }

      toast.success(t('contracts.updateSuccess'));
      setIsEditOpen(false);
      fetchData(); // Refresh list
    } catch (error) {
      console.error('Update error:', error);
      toast.error(t('contracts.updateError') || 'Update failed');
    }
  };

  const handleExtendDate = async (months) => {
    if (!editingContract?.id) return;
    try {
      const res = await contractsAPI.renew(editingContract.id, { months, copy_files: true });
      toast.success(t('contracts.updateSuccess') || 'Updated');
      setIsEditOpen(false);
      await fetchData();
      const newId = res?.data?.id;
      if (newId) {
        // Open the renewed (new) contract details
        handleViewDetail({ id: newId });
      }
    } catch (e) {
      toast.error(t('contracts.updateError') || 'Update failed');
    }
  };

  const handleViewDetail = async (contract) => {
    try {
      const response = await contractsAPI.getById(contract.id);
      setSelectedContract(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error(t('contracts.detailLoadError'));
    }
  };

  const handleExport = async (type) => {
    console.log('Starting export for type:', type);

    // Check if user is authenticated
    const token = localStorage.getItem('auth-storage');
    console.log('Auth token exists:', !!token);

    if (!token) {
      console.error('No auth token found');
      toast.error('Bitte melden Sie sich zuerst an');
      return;
    }

    try {
      // Build export parameters from current filters and sorting
      const exportParams = {
        include_expired: includeExpired,
        extended: includeExtended ? true : undefined,
        customer: filters.customer || undefined,
        days: filters.days || undefined,
        time: filters.time || undefined,
        operator: filters.operator || undefined,
        contractType: filters.contractType || undefined,
        company: filters.company || undefined,
        orderNo: filters.orderNo || undefined,
        contractNo: filters.contractNo || undefined,
        description: filters.description || undefined,
        sortField: sortField || undefined,
        sortDirection: sortDirection || undefined,
        lang: i18n.language,
      };

      // Remove undefined values
      Object.keys(exportParams).forEach(key =>
        exportParams[key] === undefined && delete exportParams[key]
      );

      console.log('Export params:', exportParams);

      let response;
      let filename;

      switch (type) {
        case 'excel':
          console.log('Calling exportExcel API with params:', exportParams);
          response = await contractsAPI.exportExcel(exportParams);
          console.log('Excel API response received');
          filename = 'contracts.xlsx';
          break;
        case 'csv':
          console.log('Calling exportCSV API with params:', exportParams);
          response = await contractsAPI.exportCSV(exportParams);
          console.log('CSV API response received');
          filename = 'contracts.csv';
          break;
        case 'pdf':
          console.log('Calling exportPDF API with params:', exportParams);
          response = await contractsAPI.exportPDF(exportParams);
          console.log('PDF API response received');
          filename = 'contracts.pdf';
          break;
        default:
          console.log('Unknown export type:', type);
          return;
      }

      console.log('API response received, creating download...', response);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      console.log('Export completed successfully');
      toast.success(t('contracts.exportSuccess'));
    } catch (error) {
      console.error('Export failed with error:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      toast.error(t('contracts.exportError'));
    }
  };

  const handlePrint = async () => {
    try {
      // Fetch all contracts with current filters (like export does)
      let allContracts = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 200;

      while (hasMore) {
        const response = await contractsAPI.getPaged({
          skip: (page - 1) * pageSize,
          limit: pageSize,
          include_expired: includeExpired,
          extended: includeExtended ? true : undefined,
          customer: filters.customer || undefined,
          days: filters.days || undefined,
          time: filters.time || undefined,
          operator: filters.operator || undefined,
          contractType: filters.contractType || undefined,
          company: filters.company || undefined,
          orderNo: filters.orderNo || undefined,
          contractNo: filters.contractNo || undefined,
          description: filters.description || undefined,
          sortField: sortField || 'days_remaining',
          sortDirection: sortDirection || 'asc',
        });

        const items = response.data?.items || [];
        allContracts = [...allContracts, ...items];
        hasMore = items.length === pageSize;
        page++;
      }

      const getExtLabel = (months) => {
        const m = Number(months);
        if (!Number.isFinite(m) || m <= 0) return '';
        if (m % 12 === 0) {
          const years = m / 12;
          return `(+${years} ${t('contracts.years', { count: years })})`;
        }
        return `(+${m} ${t('contracts.months', { count: m })})`;
      };

      const formatDaysRemaining = (contract) => {
        const daysRemaining = contract.days_remaining;
        const isGrouped = includeExtended && (
          contract.latest_days_remaining !== undefined ||
          contract.latest_extension_months !== undefined
        );
        const daysToShow = isGrouped && contract.latest_days_remaining !== undefined
          ? contract.latest_days_remaining
          : daysRemaining;
        const extMonths = isGrouped && contract.latest_extension_months !== undefined
          ? contract.latest_extension_months
          : includeExtended && contract.extended ? contract.extended_months : null;

        if (daysToShow < 0) {
          const base = t('contracts.expired');
          return extMonths ? `${base} ${getExtLabel(extMonths)}` : base;
        }
        return extMonths ? `${daysToShow} ${getExtLabel(extMonths)}` : String(daysToShow);
      };

      const rows = allContracts.map(c => `
        <tr>
          <td>${c.customer_name || '-'}</td>
          <td>${formatDaysRemaining(c)}</td>
          <td>${c.contract_type_name || '-'}</td>
          <td>${c.company_name || '-'}</td>
          <td>${c.order_number || '-'}</td>
          <td>${c.contract_number || '-'}</td>
          <td>${c.description || '-'}</td>
        </tr>
      `).join('');

      // Create hidden iframe for printing within current page
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${t('contracts.title')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background: #f0f0f0; font-weight: bold; }
            @media print { body { -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <h1>${t('contracts.title')}</h1>
          <table>
            <thead>
              <tr>
                <th>${t('contracts.customer')}</th>
                <th>${t('contracts.daysRemaining')}</th>
                <th>${t('contracts.type')}</th>
                <th>${t('contracts.company')}</th>
                <th>${t('contracts.orderNo')}</th>
                <th>${t('contracts.contractNo')}</th>
                <th>${t('contracts.description')}</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
        </html>
      `);
      doc.close();

      // Wait for content to load, then print
      iframe.contentWindow.focus();
      iframe.contentWindow.print();

      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);

    } catch (error) {
      console.error('Print error:', error);
      toast.error(t('contracts.printError'));
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalFound / itemsPerPage);
  const currentContracts = contracts;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      if (currentPage > 3) pages.push('...');

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) pages.push(i);

      if (currentPage < totalPages - 2) pages.push('...');

      pages.push(totalPages);
    }

    return pages;
  };

  const getRowClass = (contract) => {
    if (includeExtended && contract.extended) return 'bg-green-50/50 dark:bg-green-900/20';
    if (contract.days_remaining < 0) return 'bg-red-50/50 dark:bg-red-900/20';
    if (contract.days_remaining <= 7) return 'bg-orange-50/50 dark:bg-orange-900/20';
    if (contract.days_remaining <= 30) return 'bg-yellow-50/50 dark:bg-yellow-900/10';
    return '';
  };

  const getExtensionLabel = (months) => {
    const m = Number(months);
    if (!Number.isFinite(m) || m <= 0) return '';

    // Prefer years for full-year extensions
    if (m % 12 === 0) {
      const years = m / 12;
      return `(+${t('contracts.extensionYears', { count: years })})`;
    }

    return `(+${t('contracts.extensionMonths', { count: m })})`;
  };


  const getStatusBadge = (status, daysRemaining) => {
    if (daysRemaining < 0) {
      return <Badge variant="destructive">{t('contracts.expired')}</Badge>;
    }
    if (status === 'cancelled') {
      return <Badge variant="secondary">{t('contracts.cancelled')}</Badge>;
    }
    return <Badge className="bg-green-500">{t('contracts.active')}</Badge>;
  };

  const SortIcon = ({ colKey }) => {
    const active = sortField === colKey;
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          "h-6 w-6 text-muted-foreground hover:text-foreground",
          active && "text-foreground dark:text-white",
          "dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/10"
        )}
        onClick={() => handleSort(colKey)}
        aria-label={`Sort ${colKey} ${active ? sortDirection : ''}`.trim()}
      >
        <ArrowUpDown className="h-3.5 w-3.5" />
      </Button>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="contracts-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight dark:text-white">{t('contracts.title')}</h1>
          <p className="text-muted-foreground dark:text-gray-400 mt-1">
            {t('contracts.manage')}
          </p>
        </div>

        {/* Stats and Export Row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <p className="text-sm text-muted-foreground dark:text-gray-400">
            <span className="font-semibold text-foreground dark:text-gray-200">{t('contracts.recordsFound', { count: totalFound })}</span>.
            {t('contracts.totalRecords', { count: totalTotal })}
          </p>

          <div className="flex flex-wrap items-center gap-3" data-print-hide="true">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('excel')}
                className="bg-green-600 hover:bg-green-700 text-white border-0"
              >
                Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('csv')}
                className="bg-blue-600 hover:bg-blue-700 text-white border-0"
              >
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('pdf')}
                className="bg-red-600 hover:bg-red-700 text-white border-0"
              >
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="bg-slate-600 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white border-0 print-keep"
              >
                {t('contracts.print')}
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeExpired"
                  checked={includeExpired}
                  onCheckedChange={handleToggleIncludeExpired}
                  className="dark:border-gray-600 dark:data-[state=checked]:bg-zinc-200 dark:data-[state=checked]:text-black"
                />
                <Label htmlFor="includeExpired" className="text-sm dark:text-gray-300">
                  {t('contracts.includeExpired')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeExtended"
                  checked={includeExtended}
                  onCheckedChange={handleToggleIncludeExtended}
                  className="dark:border-gray-600 dark:data-[state=checked]:bg-zinc-200 dark:data-[state=checked]:text-black"
                />
                <Label htmlFor="includeExtended" className="text-sm dark:text-gray-300">
                  {t('contracts.showExtended')}
                </Label>
              </div>
            </div>
          </div>
        </div>

        {/* Main Table Card */}
        <Card className="dark:bg-[#333333] dark:border-zinc-800 overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="p-8 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    {/* Column Headers */}
                    <TableRow className="table-header dark:bg-[#444444] dark:border-gray-700">
                      <TableHead className="dark:text-white">
                        <div className="flex items-center gap-1">
                          {t('contracts.customer')}
                          <SortIcon colKey="customer_name" />
                        </div>
                      </TableHead>
                      <TableHead className="dark:text-white">
                        <div className="flex items-center gap-1">
                          {t('contracts.daysRemaining')}
                          <SortIcon colKey="days_remaining" />
                        </div>
                      </TableHead>
                      <TableHead className="dark:text-white">
                        <div className="flex items-center gap-1">
                          {t('contracts.type')}
                          <SortIcon colKey="contract_type_name" />
                        </div>
                      </TableHead>
                      <TableHead className="dark:text-white">
                        <div className="flex items-center gap-1">
                          {t('contracts.company')}
                          <SortIcon colKey="company_name" />
                        </div>
                      </TableHead>
                      <TableHead className="dark:text-white">
                        <div className="flex items-center gap-1">
                          {t('contracts.orderNo')}
                          <SortIcon colKey="order_number" />
                        </div>
                      </TableHead>
                      <TableHead className="dark:text-white">
                        <div className="flex items-center gap-1">
                          {t('contracts.contractNo')}
                          <SortIcon colKey="contract_number" />
                        </div>
                      </TableHead>
                      <TableHead className="dark:text-white">
                        <div className="flex items-center gap-1">
                          {t('contracts.description')}
                        </div>
                      </TableHead>
                      <TableHead className="w-[150px] bg-muted/50 dark:bg-white/5">
                        <Button
                          className="w-full bg-zinc-700 hover:bg-zinc-600 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-white shadow-sm"
                          onClick={() => navigate('/contracts/new')}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          <span className="whitespace-nowrap">{t('contracts.addContract')}</span>
                        </Button>
                      </TableHead>
                    </TableRow>

                    {/* Filter Row */}
                    <TableRow className="bg-blue-50/70 dark:bg-blue-950/30 border-y-2 border-blue-300 dark:border-blue-800">
                      <TableHead className="py-2">
                        <ClearableFilterInput
                          placeholder={t('contracts.customer')}
                          value={filters.customer}
                          onChange={(e) => handleFilterChange('customer', e.target.value)}
                          onClear={() => handleFilterChange('customer', '')}
                          clearLabel={t('contracts.clearFilter')}
                          className="pl-7"
                          leftIcon={<Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-blue-500 dark:text-blue-400 pointer-events-none z-10" />}
                        />
                      </TableHead>
                      <TableHead className="py-2">
                        <div className="flex gap-1">
                          <ClearableFilterInput
                            placeholder={t('contracts.days')}
                            value={filters.days}
                            onChange={(e) => handleFilterChange('days', e.target.value)}
                            onClear={() => handleFilterChange('days', '')}
                            clearLabel={t('contracts.clearFilter')}
                            className="w-20"
                          />
                          <select
                            className="flex h-8 w-24 rounded-md border border-blue-300 bg-white px-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[#333333] dark:border-blue-700 dark:text-white"
                            value={filters.time}
                            onChange={(e) => handleFilterChange('time', e.target.value)}
                            aria-label={t('filters.timePlaceholder')}
                          >
                            <option value="" className="dark:bg-[#333333]">{t('filters.timePlaceholder')}</option>
                            <option value="day" className="dark:bg-[#333333]">{t('filters.unit.day')}</option>
                            <option value="week" className="dark:bg-[#333333]">{t('filters.unit.week')}</option>
                            <option value="month" className="dark:bg-[#333333]">{t('filters.unit.month')}</option>
                            <option value="year" className="dark:bg-[#333333]">{t('filters.unit.year')}</option>
                          </select>
                          <select
                            className="flex h-8 w-28 rounded-md border border-blue-300 bg-white px-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[#333333] dark:border-blue-700 dark:text-white"
                            value={filters.operator}
                            onChange={(e) => handleFilterChange('operator', e.target.value)}
                            aria-label={t('filters.opPlaceholder')}
                          >
                            <option value="" className="dark:bg-[#333333]">{t('filters.opPlaceholder')}</option>
                            <option value="eq" className="dark:bg-[#333333]">{t('filters.op.equals')} (=)</option>
                            <option value="lt" className="dark:bg-[#333333]">{t('filters.op.lt')} (&lt;)</option>
                            <option value="gt" className="dark:bg-[#333333]">{t('filters.op.gt')} (&gt;)</option>
                          </select>
                        </div>
                      </TableHead>
                      <TableHead className="py-2">
                        <ClearableFilterInput
                          placeholder={t('contracts.type')}
                          value={filters.contractType}
                          onChange={(e) => handleFilterChange('contractType', e.target.value)}
                          onClear={() => handleFilterChange('contractType', '')}
                          clearLabel={t('contracts.clearFilter')}
                        />
                      </TableHead>
                      <TableHead className="py-2">
                        <ClearableFilterInput
                          placeholder={t('contracts.company')}
                          value={filters.company}
                          onChange={(e) => handleFilterChange('company', e.target.value)}
                          onClear={() => handleFilterChange('company', '')}
                          clearLabel={t('contracts.clearFilter')}
                        />
                      </TableHead>
                      <TableHead className="py-2">
                        <ClearableFilterInput
                          placeholder={t('contracts.orderNo')}
                          value={filters.orderNo}
                          onChange={(e) => handleFilterChange('orderNo', e.target.value)}
                          onClear={() => handleFilterChange('orderNo', '')}
                          clearLabel={t('contracts.clearFilter')}
                        />
                      </TableHead>
                      <TableHead className="py-2">
                        <ClearableFilterInput
                          placeholder={t('contracts.contractNo')}
                          value={filters.contractNo}
                          onChange={(e) => handleFilterChange('contractNo', e.target.value)}
                          onClear={() => handleFilterChange('contractNo', '')}
                          clearLabel={t('contracts.clearFilter')}
                        />
                      </TableHead>
                      <TableHead className="py-2">
                        <ClearableFilterInput
                          placeholder={t('contracts.description')}
                          value={filters.description}
                          onChange={(e) => handleFilterChange('description', e.target.value)}
                          onClear={() => handleFilterChange('description', '')}
                          clearLabel={t('contracts.clearFilter')}
                        />
                      </TableHead>
                      <TableHead className="py-2 bg-muted/50 dark:bg-white/5">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={clearFilters}
                          className="w-full bg-red-800 hover:bg-red-900 text-white shadow-sm"
                        >
                          <FilterX className="w-4 h-4 mr-2" />
                          <span className="whitespace-nowrap">{t('contracts.clearFilter')}</span>
                        </Button>
                      </TableHead>

                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentContracts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12">
                          <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                          <p className="text-muted-foreground">{t('contracts.noContracts')}</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentContracts.map((contract) => (
                        <TableRow
                          key={contract.id}
                          className={cn(
                            "table-row cursor-pointer hover:bg-muted/50 dark:hover:bg-[#444444] dark:border-gray-700/50",
                            getRowClass(contract)
                          )}
                          onClick={() => handleViewDetail(contract)}
                          data-testid={`contract-row-${contract.id}`}
                        >
                          <TableCell className="table-cell font-medium dark:text-white">
                            {contract.customer_name || '-'}
                          </TableCell>
                          <TableCell className="table-cell">
                            <span className={cn(
                              "font-mono",
                              contract.days_remaining < 0 ? 'text-red-600 dark:text-red-400' :
                                contract.days_remaining <= 7 ? 'text-orange-600 dark:text-orange-400' :
                                  contract.days_remaining <= 30 ? 'text-yellow-600 dark:text-yellow-400' : 'dark:text-gray-300'
                            )}>
                              {(() => {
                                // If showing extended contracts and we have grouped data, use latest renewal info
                                // Check if this is a grouped contract (has latest_days_remaining or latest_extension_months)
                                const isGrouped = includeExtended && (
                                  contract.latest_days_remaining !== undefined ||
                                  contract.latest_extension_months !== undefined
                                );

                                const daysToShow = isGrouped && contract.latest_days_remaining !== undefined
                                  ? contract.latest_days_remaining
                                  : contract.days_remaining;
                                const extensionMonths = isGrouped && contract.latest_extension_months !== undefined
                                  ? contract.latest_extension_months
                                  : (includeExtended && contract.extended ? contract.extended_months : null);

                                if (daysToShow < 0) {
                                  return (
                                    <>
                                      {t('contracts.finishedContract')}
                                      {includeExtended && extensionMonths ? (
                                        <span className="ml-1 text-xs text-muted-foreground">
                                          {getExtensionLabel(extensionMonths)}
                                        </span>
                                      ) : null}
                                    </>
                                  );
                                }
                                return (
                                  <>
                                    {daysToShow}
                                    {includeExtended && extensionMonths ? (
                                      <span className="ml-1 text-xs text-muted-foreground">
                                        {getExtensionLabel(extensionMonths)}
                                      </span>
                                    ) : null}
                                  </>
                                );
                              })()}
                            </span>
                          </TableCell>
                          <TableCell className="table-cell dark:text-gray-300">
                            {contract.contract_type_name || '-'}
                          </TableCell>
                          <TableCell className="table-cell dark:text-gray-300">
                            {contract.company_name || '-'}
                          </TableCell>
                          <TableCell className="table-cell font-mono text-sm dark:text-gray-300">
                            {contract.order_number || '-'}
                          </TableCell>
                          <TableCell className="table-cell dark:text-gray-300">
                            {contract.contract_number || '-'}
                          </TableCell>
                          <TableCell className="table-cell text-muted-foreground dark:text-gray-400 truncate max-w-[150px]">
                            {contract.description || '-'}
                          </TableCell>

                          <TableCell className="table-cell bg-muted/30 dark:bg-white/5">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 rounded-full dark:hover:bg-[#444444] dark:text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDetail(contract);
                                }}
                                title={t('contracts.info')}
                              >
                                <Info className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 rounded-full dark:hover:bg-[#444444] dark:text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(contract);
                                }}
                                title={t('common.edit')}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 rounded-full dark:hover:bg-red-950/30 dark:text-red-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(contract);
                                }}
                                title={t('common.delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground dark:text-gray-400">{t('contracts.perPage')}</span>
                    <Select
                      value={itemsPerPage.toString()}
                      onValueChange={(val) => {
                        setItemsPerPage(Number(val));
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger className="w-20 h-8 dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-[#444444] dark:border-gray-700">
                        <SelectItem value="10" className="dark:text-white dark:focus:bg-[#555555]">10</SelectItem>
                        <SelectItem value="25" className="dark:text-white dark:focus:bg-[#555555]">25</SelectItem>
                        <SelectItem value="50" className="dark:text-white dark:focus:bg-[#555555]">50</SelectItem>
                        <SelectItem value="100" className="dark:text-white dark:focus:bg-[#555555]">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground dark:text-gray-400">{t('contracts.showRecords')}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      {t('contracts.prev')}
                    </Button>

                    {getPageNumbers().map((page, index) => (
                      page === '...' ? (
                        <span key={`ellipsis-${index}`} className="px-2 dark:text-gray-400">...</span>
                      ) : (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          className={cn(
                            "w-8 h-8 p-0",
                            currentPage === page
                              ? "dark:bg-white dark:text-[#333333] dark:hover:bg-gray-200"
                              : "dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]"
                          )}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      )
                    ))}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]"
                    >
                      {t('contracts.next')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] p-0 overflow-hidden flex flex-col dark:bg-[#333333] dark:border-gray-700 dark:text-white" data-testid="contract-detail-modal">
            <DialogHeader className="px-6 py-4 border-b dark:border-gray-700">
              <DialogTitle className="text-lg font-normal dark:text-white">
                {t('contracts.detailTitle')}
              </DialogTitle>
            </DialogHeader>

            {selectedContract && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 scrollbar-thin">
                  <div className="space-y-2">
                    <div className="flex flex-col">
                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="font-semibold text-sm dark:text-gray-200">{t('contracts.customer')}:</span>
                        <div className="flex items-center gap-1">
                          <span className="text-sm dark:text-gray-300">{selectedContract.customer_name || '-'}</span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="font-semibold text-sm dark:text-gray-200">{t('contracts.type')}:</span>
                        <span className="text-sm dark:text-gray-300">{selectedContract.contract_type_name || '-'}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="font-semibold text-sm dark:text-gray-200">{t('contracts.company')}:</span>
                        <span className="text-sm dark:text-gray-300">{selectedContract.company_name || '-'}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="font-semibold text-sm dark:text-gray-200">{t('contracts.startDate')}:</span>
                        <span className="text-sm dark:text-gray-300">{selectedContract.start_date}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="font-semibold text-sm dark:text-gray-200">{t('contracts.endDate')}:</span>
                        <span className="text-sm dark:text-gray-300">{selectedContract.end_date}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="font-semibold text-sm dark:text-gray-200">{t('contracts.daysRemaining')}:</span>
                        <span className="text-sm dark:text-gray-300">{selectedContract.days_remaining} {t('contracts.days')}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="font-semibold text-sm dark:text-gray-200">{t('contracts.orderNo')}:</span>
                        <span className="text-sm dark:text-gray-300">{selectedContract.order_number || '-'}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="font-semibold text-sm dark:text-gray-200">{t('contracts.contractNo')}:</span>
                        <span className="text-sm dark:text-gray-300">{selectedContract.contract_number || '-'}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-700/50">
                        <span className="font-semibold text-sm dark:text-gray-200">{t('contracts.description')}:</span>
                        <span className="text-sm dark:text-gray-300">{selectedContract.description || '-'}</span>
                      </div>
                    </div>

                    <div className="border rounded-lg p-2 dark:border-gray-700 bg-muted/20 dark:bg-white/5 mt-2">
                      <h3 className="font-semibold mb-1 text-sm dark:text-white">{t('contracts.history')}:</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('contracts.noHistory')}</p>
                    </div>

                    <div className="border rounded-lg p-2 dark:border-gray-700 bg-muted/20 dark:bg-white/5 mt-2">
                      <h3 className="font-semibold mb-1 text-sm dark:text-white">{t('contracts.contractFiles')}:</h3>
                      <div className="grid grid-cols-1 gap-2 mt-2">
                        {selectedContract.files && selectedContract.files.length > 0 ? (
                          selectedContract.files.map((file, index) => (
                            <div
                              key={index}
                              className="border rounded-lg p-4 flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/10 transition-colors bg-white dark:bg-white/5 dark:border-gray-700"
                              onClick={async () => {
                                if (file.url) {
                                  try {
                                    const response = await contractsAPI.downloadFile(file.url);
                                    const contentType = response.headers['content-type'] || 'application/octet-stream';
                                    const blob = new Blob([response.data], { type: contentType });
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    window.open(blobUrl, '_blank');
                                  } catch (error) {
                                    console.error('File download error:', error);
                                    toast.error(t('contracts.fileOpenError', { defaultValue: 'Error opening file' }));
                                  }
                                } else {
                                  toast.info(t('contracts.noFileUrl', { defaultValue: 'File not available' }));
                                }
                              }}
                            >
                              <div className="w-12 h-16 border-2 border-gray-300 dark:border-gray-600 rounded flex items-center justify-center bg-white dark:bg-[#444444]">
                                <FileText className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                              </div>
                              <div className="text-center">
                                <p className="font-bold text-sm dark:text-white truncate max-w-[200px]">{file.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {file.size ? (parseInt(file.size) / 1024 / 1024).toFixed(2) : '0.16'} MB
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-gray-400 py-4 text-center">
                            {t('contracts.noFilesUploaded', { defaultValue: 'No files uploaded' })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6 pt-4 border-t dark:border-gray-700 flex justify-end bg-white dark:bg-[#333333]">
                  <Button
                    variant="outline"
                    onClick={() => setIsDetailOpen(false)}
                    className="bg-gray-500 hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 text-white border-0"
                  >
                    {t('contracts.close')}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Contract Modal */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0 overflow-hidden dark:bg-[#333333] dark:border-gray-700" data-testid="contract-edit-modal">
            <DialogHeader className="px-6 py-4 border-b dark:border-gray-700">
              <DialogTitle className="text-lg font-normal dark:text-white">
                {t('contracts.editTitle')}
              </DialogTitle>
            </DialogHeader>

            {editingContract && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="space-y-4">
                    {/* Contract Type Select */}
                    {(() => {
                      const uniqueContractTypes = Array.from(
                        new Map(
                          (contractTypes || []).map((t) => [`${t.company_id || ''}::${t.name || ''}`, t])
                        ).values()
                      );
                      return (
                        <div className="space-y-1.5">
                          <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.selectContractType')}</Label>
                          <Select
                            value={editingContract.contract_type_id?.toString()}
                            onValueChange={(val) => setEditingContract({ ...editingContract, contract_type_id: val })}
                          >
                            <SelectTrigger className="h-9 dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                              <SelectValue placeholder={t('contracts.type')} />
                            </SelectTrigger>
                            <SelectContent className="dark:bg-[#444444] dark:border-gray-700">
                              {uniqueContractTypes.map(type => (
                                <SelectItem key={type.id} value={type.id.toString()} className="dark:text-white dark:focus:bg-[#555555]">{type.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })()}

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.selectCompanyLabel')}</Label>
                      <Select
                        value={editingContract.company_id?.toString()}
                        onValueChange={(val) => setEditingContract({ ...editingContract, company_id: val })}
                      >
                        <SelectTrigger className="h-9 dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                          <SelectValue placeholder={t('contracts.company')} />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-[#444444] dark:border-gray-700">
                          {companies.map(company => (
                            <SelectItem key={company.id} value={company.id.toString()} className="dark:text-white dark:focus:bg-[#555555]">{company.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.startDate')}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full justify-start text-left font-normal dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]",
                              !editingContract.start_date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editingContract.start_date ? (
                              format(new Date(editingContract.start_date), "PPP", { locale: currentLocale })
                            ) : (
                              <span>{t('contracts.startDate')}</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 dark:bg-[#333333] dark:border-gray-700" align="start">
                          <Calendar
                            mode="single"
                            selected={editingContract.start_date ? new Date(editingContract.start_date) : undefined}
                            onSelect={(date) => {
                              if (date) {
                                setEditingContract({ ...editingContract, start_date: format(date, 'yyyy-MM-dd') });
                              }
                            }}
                            initialFocus
                            captionLayout="dropdown-buttons"
                            fromYear={2000}
                            toYear={new Date().getFullYear() + 10}
                            locale={currentLocale}
                            className="dark:bg-[#333333] dark:text-white"
                            classNames={{
                              day_selected: "bg-zinc-900 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200",
                              day_today: "bg-accent text-accent-foreground dark:bg-white/10",
                            }}
                            components={{
                              Dropdown: ({ value, onChange, children, ...props }) => {
                                const options = React.Children.toArray(children)
                                const selected = options.find((child) => child.props.value === value)
                                const handleChange = (value) => {
                                  const changeEvent = {
                                    target: { value },
                                  }
                                  onChange?.(changeEvent)
                                }
                                return (
                                  <Select
                                    value={value?.toString()}
                                    onValueChange={(value) => {
                                      handleChange(value)
                                    }}
                                  >
                                    <SelectTrigger className="pr-1.5 focus:ring-0 h-[28px] w-auto gap-1 border-none shadow-none font-medium">
                                      <SelectValue>{selected?.props?.children}</SelectValue>
                                    </SelectTrigger>
                                    <SelectContent position="popper" className="max-h-[300px]">
                                      {options.map((option, id) => (
                                        <SelectItem key={`${option.props.value}-${id}`} value={option.props.value?.toString() ?? ""}>
                                          {option.props.children}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )
                              },
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.endDate')}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full justify-start text-left font-normal dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]",
                              !editingContract.end_date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editingContract.end_date ? (
                              format(new Date(editingContract.end_date), "PPP", { locale: currentLocale })
                            ) : (
                              <span>{t('contracts.endDate')}</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 dark:bg-[#333333] dark:border-gray-700" align="start">
                          <Calendar
                            mode="single"
                            selected={editingContract.end_date ? new Date(editingContract.end_date) : undefined}
                            onSelect={(date) => {
                              if (date) {
                                setEditingContract({ ...editingContract, end_date: format(date, 'yyyy-MM-dd') });
                              }
                            }}
                            initialFocus
                            captionLayout="dropdown-buttons"
                            fromYear={2000}
                            toYear={new Date().getFullYear() + 10}
                            locale={currentLocale}
                            className="dark:bg-[#333333] dark:text-white"
                            classNames={{
                              day_selected: "bg-zinc-900 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200",
                              day_today: "bg-accent text-accent-foreground dark:bg-white/10",
                            }}
                            components={{
                              Dropdown: ({ value, onChange, children, ...props }) => {
                                const options = React.Children.toArray(children)
                                const selected = options.find((child) => child.props.value === value)
                                const handleChange = (value) => {
                                  const changeEvent = {
                                    target: { value },
                                  }
                                  onChange?.(changeEvent)
                                }
                                return (
                                  <Select
                                    value={value?.toString()}
                                    onValueChange={(value) => {
                                      handleChange(value)
                                    }}
                                  >
                                    <SelectTrigger className="pr-1.5 focus:ring-0 h-[28px] w-auto gap-1 border-none shadow-none font-medium">
                                      <SelectValue>{selected?.props?.children}</SelectValue>
                                    </SelectTrigger>
                                    <SelectContent position="popper" className="max-h-[300px]">
                                      {options.map((option, id) => (
                                        <SelectItem key={`${option.props.value}-${id}`} value={option.props.value?.toString() ?? ""}>
                                          {option.props.children}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )
                              },
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.extendDate')}</Label>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="rounded-full h-7 text-xs dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]" onClick={() => handleExtendDate(6)}>
                          {t('contracts.6months')}
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-full h-7 text-xs dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]" onClick={() => handleExtendDate(12)}>
                          {t('contracts.1year')}
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-full h-7 text-xs dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]" onClick={() => handleExtendDate(24)}>
                          {t('contracts.2years')}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.orderNo')}</Label>
                      <Input
                        value={editingContract.order_number || ''}
                        onChange={(e) => setEditingContract({ ...editingContract, order_number: e.target.value })}
                        className="h-9 bg-gray-50 dark:bg-zinc-800 dark:text-gray-400 dark:border-gray-700"
                        readOnly
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.contractNo')}</Label>
                      <Input
                        placeholder={t('contracts.contractNoPlaceholder')}
                        value={editingContract.contract_number || ''}
                        onChange={(e) => setEditingContract({ ...editingContract, contract_number: e.target.value })}
                        className="h-9 dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.descriptionOptional')}</Label>
                      <textarea
                        className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px] dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                        value={editingContract.description || ''}
                        onChange={(e) => setEditingContract({ ...editingContract, description: e.target.value })}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.filesOptional')}</Label>

                      {/* File List Container */}
                      {((editingContract.files && editingContract.files.length > 0) || selectedFiles.length > 0) && (
                        <div className="space-y-3 pt-2 mb-4">
                          {/* List Existing Files */}
                          {editingContract.files && editingContract.files.map((file, idx) => (
                            <div key={`existing-${idx}`} className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center justify-between bg-white dark:bg-[#444444]/30 shadow-sm">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-16 border-2 border-gray-100 dark:border-gray-600 rounded-lg flex items-center justify-center bg-white dark:bg-[#444444] shadow-sm">
                                  <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-[250px]">{file.name}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {file.size ? (parseInt(file.size) / 1024 / 1024).toFixed(2) : '0.16'} MB
                                  </span>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteExistingFile(file.id);
                                }}
                              >
                                <X className="w-5 h-5" />
                              </Button>
                            </div>
                          ))}

                          {/* Show Newly Selected Files */}
                          {selectedFiles.map((file, idx) => (
                            <div key={`new-${idx}`} className="border-2 border-dashed border-blue-200 dark:border-blue-900/30 rounded-xl p-4 flex items-center justify-between bg-blue-50/30 dark:bg-blue-900/10 shadow-sm group">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-16 border-2 border-blue-100 dark:border-blue-800 rounded-lg flex items-center justify-center bg-white dark:bg-[#444444] shadow-sm">
                                  <span className="text-blue-600 dark:text-blue-400 font-bold text-xs uppercase">New</span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-[250px]">{file.name}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </span>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
                                }}
                              >
                                <X className="w-5 h-5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Upload Area - Always shown to allow adding more files */}
                      <div
                        className={cn(
                          "relative border-2 border-dashed rounded-lg p-12 text-center flex flex-col items-center justify-center gap-4 cursor-pointer transition-all",
                          dragActive
                            ? "border-blue-500 bg-blue-50/30 dark:border-blue-400 dark:bg-blue-400/10 scale-[1.01]"
                            : "border-green-700/50 bg-green-50/20 dark:bg-white/5 dark:border-gray-700"
                        )}
                        onClick={() => fileInputRef.current?.click()}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                      >
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                          multiple
                          onChange={handleFileChange}
                        />
                        <UploadCloud className={cn("w-12 h-12 transition-colors", dragActive ? "text-blue-500 dark:text-blue-400" : "text-green-700 dark:text-gray-400")} />
                        <div>
                          <p className="font-semibold text-zinc-800 dark:text-white text-lg">{t('contract.dragDrop')}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-1">{t('contract.supportedFormats')}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('contract.maxSize')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 pt-4 border-t dark:border-gray-700 flex justify-end gap-2 bg-white dark:bg-[#333333]">
                  <Button
                    variant="outline"
                    onClick={() => setIsEditOpen(false)}
                    className="bg-gray-500 hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 text-white border-0"
                  >
                    {t('contracts.close')}
                  </Button>
                  <Button
                    className="bg-zinc-800 hover:bg-zinc-700 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-white"
                    onClick={handleSaveChanges}
                  >
                    {t('contracts.update')}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="sm:max-w-md dark:bg-[#333333] dark:border-none dark:text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl font-bold dark:text-white">
                {t('contracts.deleteModalTitle')}
              </DialogTitle>
            </DialogHeader>

            <div className="py-4">
              <p className="text-gray-700 dark:text-gray-300">
                {t('contracts.deleteModalMessage')}
                {contractToDelete && (
                  <span className="font-bold text-red-500">
                    {contractToDelete.contract_number} ({contractToDelete.company_name})
                  </span>
                )}?
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="destructive"
                className="bg-red-600 hover:bg-red-700 dark:bg-red-900 dark:hover:bg-red-800 text-white"
                onClick={handleDeleteConfirm}
              >
                {t('common.confirm', { defaultValue: 'Yes' })}
              </Button>
              <Button
                variant="secondary"
                className="bg-gray-500 hover:bg-gray-600 dark:bg-[#444444] dark:hover:bg-[#555555] text-white"
                onClick={() => setDeleteModalOpen(false)}
              >
                {t('common.cancel', { defaultValue: 'No' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div >
    </Layout >
  );
}
